import axios from 'axios';

const apiClient = axios.create({
  baseURL: '',
  timeout: 10000,
});

// Auth state change callback — set by AuthProvider
let onAuthError = null;
export function setAuthErrorHandler(handler) {
  onAuthError = handler;
}

apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('bragsheet_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Apply longer timeout for AI endpoints
    if (config.url && config.url.match(/\/api\/ai\//)) {
      config.timeout = 30000;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Retry logic with exponential backoff
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxJitter: 500,
  retryableMethods: ['get', 'put', 'delete'],
};

function shouldRetry(error) {
  const method = (error.config?.method || '').toLowerCase();
  if (!RETRY_CONFIG.retryableMethods.includes(method)) return false;

  // Network error (no response at all)
  if (!error.response) return true;

  const status = error.response.status;
  // Retry on 429 or 5xx
  if (status === 429 || status >= 500) return true;

  return false;
}

function getRetryDelay(retryCount) {
  const exponentialDelay = RETRY_CONFIG.baseDelay * Math.pow(2, retryCount);
  const jitter = Math.random() * RETRY_CONFIG.maxJitter;
  return exponentialDelay + jitter;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401 immediately — no retry
    if (error.response && error.response.status === 401) {
      if (onAuthError) {
        onAuthError();
      }
      return Promise.reject(error);
    }

    const config = error.config;
    if (!config) return Promise.reject(error);

    config.__retryCount = config.__retryCount || 0;

    if (shouldRetry(error) && config.__retryCount < RETRY_CONFIG.maxRetries) {
      config.__retryCount += 1;
      const delay = getRetryDelay(config.__retryCount - 1);
      await wait(delay);
      return apiClient(config);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
